import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  HiChevronLeft,
  HiChevronDown,
  HiChevronUp,
  HiPencilSquare,
} from "react-icons/hi2";
import Header from "../components/layout/Header";
import BottomNav from "../components/layout/BottomNav";
import { useAuth } from "../contexts/AuthContext";
import { useAthletePrograms } from "../hooks/useAthletePrograms";
import { formatAssigner } from "../services/programService";
import { sessionService } from "../services/sessionService";
import { trainingTypeService } from "../services/trainingTypeService";
import { TrainingType } from "../types/activity";
import { Session } from "../types/post";
import {
  buildProgramGrid,
  isSessionMoved,
  ProgramGridWeek,
  toLocalDateString,
} from "../utils/programProgress";
import {
  periodisationLabel,
  resolvePeriodisationColor,
} from "../utils/scheduleColors";
import { canChangeSessionDate } from "../utils/sessionRules";
import { asDisplayNameOrNull } from "../utils/displayName";
import {
  SessionCard,
  buildSessionVM,
  resolveSessionTypeName,
} from "../components/schedule";
import SessionBottomSheet from "../components/sessions/SessionBottomSheet";
import EditSessionModal, {
  buildDateChangeUpdates,
} from "../components/sessions/EditSessionModal";
import {
  ProgramProgressRing,
  ProgramStateChip,
} from "../components/programs/ProgramProgress";
import {
  formatDayLabel,
  formatProgramDateRange,
  formatWeeks,
} from "../components/programs/programFormat";

type LoadedSession = Session & { id: string };

/**
 * Read-only program detail (plan 3g): header with assigner, weeks, duration
 * and a progress ring; weeks as sections and days as rows built from
 * `plannedDate` (decision 3). Tapping a session opens the shared
 * `SessionBottomSheet` (Open / Link / Change date), like the Schedule.
 */
const ProgramDetail: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get("org") || undefined;
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { programs, loading, error, patchSession, removeSession } =
    useAthletePrograms(currentUser?.uid);

  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [sheetSession, setSheetSession] = useState<LoadedSession | null>(null);
  const [dateChangeSession, setDateChangeSession] =
    useState<LoadedSession | null>(null);
  const [openWeeks, setOpenWeeks] = useState<Set<number> | null>(null);

  useEffect(() => {
    trainingTypeService
      .fetchTrainingTypes()
      .then(setTrainingTypes)
      .catch((e) => console.error("Error fetching training types:", e));
  }, []);

  const program = useMemo(
    () =>
      programs.find(
        (p) =>
          p.id === programId &&
          (organisationId
            ? p.organisationId === organisationId
            : p.source === "self"),
      ) ?? programs.find((p) => p.id === programId),
    [programs, programId, organisationId],
  );

  const grid = useMemo(
    () => (program ? buildProgramGrid(program, program.sessions) : null),
    [program],
  );

  const todayStr = toLocalDateString(new Date());

  // Past weeks start collapsed; the current and future weeks start open.
  useEffect(() => {
    if (!grid || openWeeks) return;
    const open = new Set<number>();
    for (const week of grid.weeks) {
      if (week.endDate >= todayStr) open.add(week.weekNumber);
    }
    if (open.size === 0) grid.weeks.forEach((w) => open.add(w.weekNumber));
    setOpenWeeks(open);
  }, [grid, openWeeks, todayStr]);

  const toggleWeek = (weekNumber: number) =>
    setOpenWeeks((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(weekNumber)) next.delete(weekNumber);
      else next.add(weekNumber);
      return next;
    });

  const findSession = (id: string) =>
    program?.sessions.find((s) => s.id === id) ?? null;

  const handleReschedule = async (sessionId: string, newPlannedDate: string) => {
    if (!currentUser?.uid) return;
    const session = findSession(sessionId);
    if (!session || !canChangeSessionDate(session)) return;
    if (!newPlannedDate || session.plannedDate === newPlannedDate) return;
    const updates = buildDateChangeUpdates(session, newPlannedDate);
    patchSession(sessionId, updates);
    try {
      await sessionService.updateSession(currentUser.uid, sessionId, updates);
    } catch (e) {
      console.error("Error rescheduling session:", e);
      patchSession(sessionId, {
        plannedDate: session.plannedDate,
        startTime: session.startTime,
        endTime: session.endTime,
      });
      throw e;
    }
  };

  const openChangeDate = (session: LoadedSession) => {
    if (!canChangeSessionDate(session)) return;
    setSheetSession(null);
    setDateChangeSession(session);
  };

  const trainingTypeColors = userData?.trainingTypeColors;
  const periodisationColors = userData?.periodisationColors;

  const renderSessionRow = (session: LoadedSession, showDate: boolean) => {
    const vm = buildSessionVM(session, trainingTypes);
    const moved = program ? isSessionMoved(program.startDate, session) : false;
    return (
      <div key={session.id} className="relative">
        <SessionCard
          session={vm}
          variant={showDate ? "list" : "compact"}
          trainingTypeColors={trainingTypeColors}
          onClick={() => setSheetSession(session)}
        />
        {moved && (
          <span className="absolute -top-2 right-3 inline-flex items-center rounded-full bg-info/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-info font-grotesk">
            Moved
          </span>
        )}
      </div>
    );
  };

  const renderWeek = (week: ProgramGridWeek<LoadedSession>) => {
    if (!program) return null;
    const phase = program.weeks.find(
      (w) => w.weekNumber === week.weekNumber,
    )?.periodisation;
    const sessions = week.days.flatMap((d) => d.sessions);
    const done = sessions.filter((s) => s.status === "complete").length;
    const isCurrent =
      week.startDate <= todayStr && todayStr <= week.endDate;
    const isOpen = openWeeks?.has(week.weekNumber) ?? true;

    return (
      <section
        key={week.weekNumber}
        className={`rounded-xl border bg-base-100 ${
          isCurrent ? "border-primary/40 ring-1 ring-primary/20" : "border-base-300"
        }`}
      >
        <button
          type="button"
          onClick={() => toggleWeek(week.weekNumber)}
          aria-expanded={isOpen}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-h-11"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-base-content font-heading">
                Week {week.weekNumber}
              </h2>
              {isCurrent && (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary font-grotesk">
                  This week
                </span>
              )}
              {phase && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium font-grotesk text-base-content/70 bg-base-200"
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{
                      backgroundColor: resolvePeriodisationColor(
                        phase,
                        periodisationColors,
                      ),
                    }}
                    aria-hidden
                  />
                  {periodisationLabel(phase)}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-base-content/60 font-grotesk">
              <span className="font-geist tabular-nums">
                {formatProgramDateRange(week.startDate, week.endDate)}
              </span>
              <span className="text-base-content/30"> · </span>
              <span className="font-geist tabular-nums">
                {done}/{sessions.length}
              </span>{" "}
              done
            </p>
          </div>
          {isOpen ? (
            <HiChevronUp className="text-lg text-base-content/40 flex-shrink-0" />
          ) : (
            <HiChevronDown className="text-lg text-base-content/40 flex-shrink-0" />
          )}
        </button>

        {isOpen && (
          <div className="border-t border-base-300 px-4 py-3 space-y-2">
            {week.days.map((day) => {
              const isToday = day.date === todayStr;
              if (day.sessions.length === 0) {
                return (
                  <div
                    key={day.date}
                    className={`flex items-center justify-between px-1 py-1 text-xs font-grotesk ${
                      isToday ? "text-primary" : "text-base-content/40"
                    }`}
                  >
                    <span className="font-geist">{formatDayLabel(day.date)}</span>
                    <span>Rest</span>
                  </div>
                );
              }
              return (
                <div key={day.date}>
                  <div
                    className={`flex items-center gap-2 px-1 pb-1.5 pt-1 text-xs font-medium font-grotesk ${
                      isToday ? "text-primary" : "text-base-content/60"
                    }`}
                  >
                    <span className="font-geist">{formatDayLabel(day.date)}</span>
                    {isToday && <span>· Today</span>}
                  </div>
                  <div className="space-y-2">
                    {day.sessions.map((s) => renderSessionRow(s, false))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const sheetTypeName = sheetSession
    ? resolveSessionTypeName(sheetSession, trainingTypes)
    : null;

  return (
    <div className="min-h-screen bg-base-200">
      <Header />
      <main className="max-w-3xl mx-auto pt-20 pb-28 px-4 sm:px-0">
        <button
          type="button"
          onClick={() => navigate("/programs")}
          className="inline-flex items-center gap-1 min-h-11 pr-3 text-sm text-base-content/70 hover:text-base-content font-grotesk"
        >
          <HiChevronLeft className="w-5 h-5" />
          Programs
        </button>

        {loading ? (
          <div className="animate-pulse space-y-3 mt-2">
            <div className="rounded-xl border border-base-300 bg-base-100 p-5 h-36" />
            <div className="rounded-xl border border-base-300 bg-base-100 p-5 h-24" />
            <div className="rounded-xl border border-base-300 bg-base-100 p-5 h-24" />
          </div>
        ) : error ? (
          <div
            className="mt-2 rounded-xl border border-error/30 bg-error/10 px-4 py-3 text-sm text-error font-grotesk"
            role="alert"
          >
            {error}
          </div>
        ) : !program || !grid ? (
          <div className="mt-2 rounded-xl border-2 border-dashed border-base-300 bg-base-100 p-10 text-center">
            <h1 className="text-lg font-semibold text-base-content font-heading">
              Program not found
            </h1>
            <p className="mt-2 text-sm text-base-content/60 font-grotesk">
              It may have been removed, or none of its sessions are on your
              schedule.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="mt-2 rounded-xl border border-base-300 bg-base-100 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-base-content font-heading break-words">
                      {program.name}
                    </h1>
                    <ProgramStateChip
                      progress={program.progress}
                      durationWeeks={program.durationWeeks}
                    />
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-sm text-base-content/70 font-grotesk">
                    {program.assigner.kind === "coach" &&
                      program.assigner.coachImageUrl && (
                        <img
                          src={program.assigner.coachImageUrl}
                          alt=""
                          className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                        />
                      )}
                    <span className="truncate">
                      Assigned by {formatAssigner(program.assigner)}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-base-content/60 font-grotesk">
                    <span className="font-geist tabular-nums">
                      {formatWeeks(program.durationWeeks)}
                    </span>
                    <span className="text-base-content/30">·</span>
                    <span className="font-geist tabular-nums">
                      {formatProgramDateRange(
                        program.progress.startDate,
                        program.progress.endDate,
                      )}
                    </span>
                    <span className="text-base-content/30">·</span>
                    <span>
                      <span className="font-geist tabular-nums">
                        {program.progress.total}
                      </span>{" "}
                      session{program.progress.total === 1 ? "" : "s"}
                    </span>
                  </div>
                  {program.fromLinkageOnly && (
                    <p className="mt-2 text-xs text-base-content/50 font-grotesk">
                      Your coach removed this program; the sessions you logged
                      are kept.
                    </p>
                  )}
                </div>
                <ProgramProgressRing progress={program.progress} />
              </div>
              {program.description && (
                <p className="mt-3 text-sm text-base-content/70 font-grotesk whitespace-pre-line">
                  {program.description}
                </p>
              )}
              {program.source === "self" && (
                <button
                  type="button"
                  onClick={() => navigate(`/programs/${program.id}`)}
                  className="btn btn-ghost btn-sm mt-3 min-h-11 h-11 rounded-xl border border-base-300 text-sm font-medium font-grotesk normal-case text-base-content/80"
                >
                  <HiPencilSquare className="text-base" />
                  Edit program
                </button>
              )}
            </header>

            {/* Weeks */}
            <div className="mt-4 space-y-3">{grid.weeks.map(renderWeek)}</div>

            {grid.outside.length > 0 && (
              <section className="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
                <h2 className="text-base font-semibold text-base-content font-heading">
                  Moved outside the program
                </h2>
                <p className="mt-0.5 mb-3 text-xs text-base-content/60 font-grotesk">
                  Rescheduled beyond the program's{" "}
                  {formatWeeks(program.durationWeeks)}.
                </p>
                <div className="space-y-2">
                  {grid.outside.map((s) => renderSessionRow(s, true))}
                </div>
              </section>
            )}

            {grid.unscheduled.length > 0 && (
              <section className="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
                <h2 className="text-base font-semibold text-base-content font-heading">
                  Not yet scheduled
                </h2>
                <div className="mt-3 space-y-2">
                  {grid.unscheduled.map((s) => renderSessionRow(s, false))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <BottomNav />

      <SessionBottomSheet
        isOpen={!!sheetSession}
        onClose={() => setSheetSession(null)}
        session={sheetSession}
        title={
          sheetSession
            ? asDisplayNameOrNull(sheetSession.name) || sheetTypeName || "Session"
            : ""
        }
        typeName={sheetTypeName}
        ownerUserId={currentUser?.uid}
        onOpenSession={(session) => {
          setSheetSession(null);
          navigate(`/session/${session.id}`);
        }}
        onLinkToLog={(session) => {
          setSheetSession(null);
          navigate(`/dashboard?date=${session.plannedDate.replace(/-/g, "")}`);
        }}
        onChangeDate={openChangeDate}
        onDelete={async (session) => {
          if (!currentUser?.uid) return;
          await sessionService.deleteSession(currentUser.uid, session.id);
          removeSession(session.id);
        }}
        onReject={async (session, reason) => {
          if (!currentUser?.uid) return;
          await sessionService.rejectProposedSession(
            currentUser.uid,
            session.id,
            reason,
          );
          patchSession(session.id, { status: "rejected" });
        }}
      />
      {dateChangeSession && (
        <EditSessionModal
          isOpen={!!dateChangeSession}
          onClose={() => setDateChangeSession(null)}
          session={dateChangeSession}
          dateOnly
          onSaveDate={handleReschedule}
          onSessionUpdated={() => setDateChangeSession(null)}
        />
      )}
    </div>
  );
};

export default ProgramDetail;
